import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { getMonitorToken } from '../../constants/HeadersAPI';

const AudioCtxCtor =
  typeof window !== 'undefined'
    ? window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    : undefined;

let sharedAudioCtx: AudioContext | null = null;

export const getSharedTabloAudioContext = (): AudioContext | null => {
  if (!AudioCtxCtor) return null;
  if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
    sharedAudioCtx = new AudioCtxCtor();
  }
  return sharedAudioCtx;
};

/** Разблокировка autoplay в Fully Kiosk / Android WebView до первого объявления. */
export const unlockTabloMedia = async (): Promise<void> => {
  if (!AudioCtxCtor) return;
  try {
    const ctx = getSharedTabloAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    // Тихий буфер — некоторые WebView требуют реального start()
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);

    // HTMLAudio unlock — критично для WebView 86
    const silent = new Audio(
      'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'
    );
    silent.volume = 0.01;
    await silent.play().catch(() => undefined);
    silent.pause();
  } catch {
    // Fully может запретить до жеста — повторим на pointerdown
  }
};

/**
 * Хуки для стабильной работы табло в Fully Kiosk на Android TV:
 * - разблокировка AudioContext
 * - refetch после выхода из сна/скрытия вкладки
 * - опциональный Screen Wake Lock
 */
export const useTabloKioskBootstrap = () => {
  const queryClient = useQueryClient();
  const hasToken = !!getMonitorToken();

  useEffect(() => {
    if (!hasToken) return;

    void unlockTabloMedia();

    const unlockOnGesture = () => {
      void unlockTabloMedia();
    };
    window.addEventListener('pointerdown', unlockOnGesture, { once: true, passive: true });
    window.addEventListener('keydown', unlockOnGesture, { once: true });
    window.addEventListener('touchstart', unlockOnGesture, { once: true, passive: true });

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void unlockTabloMedia();
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return (
            key === 'windows' ||
            key === 'monitor-created-tickets' ||
            key === 'QueueTickers' ||
            key === 'QueueHeaders' ||
            key === 'QueueVideoPlaylist' ||
            key === 'QueueTerminalAppearance'
          );
        }
      });
    };
    document.addEventListener('visibilitychange', onVisibility);

    let wakeLock: { release: () => Promise<void> } | null = null;
    const requestWakeLock = async () => {
      try {
        const nav = navigator as Navigator & {
          wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
        };
        if (!nav.wakeLock) return;
        wakeLock = await nav.wakeLock.request('screen');
      } catch {
        // Fully обычно держит экран сам; API может быть недоступен
      }
    };
    void requestWakeLock();

    const onVisibleWake = () => {
      if (document.visibilityState === 'visible') void requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisibleWake);

    return () => {
      window.removeEventListener('pointerdown', unlockOnGesture);
      window.removeEventListener('keydown', unlockOnGesture);
      window.removeEventListener('touchstart', unlockOnGesture);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('visibilitychange', onVisibleWake);
      void wakeLock?.release().catch(() => undefined);
    };
  }, [hasToken, queryClient]);
};
